import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/mutations"

import {
  type InventoryPalletHoldFailure,
  type InventoryPalletHoldInput,
  PutPalletOnHoldDocument,
  type PutPalletOnHoldMutation,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface UsePutPalletOnHoldReturn {
  putPalletOnHold: (input: InventoryPalletHoldInput) => Promise<boolean>
  isPutting: boolean
}

export const usePutPalletOnHold = (): UsePutPalletOnHoldReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation<PutPalletOnHoldMutation, { input: InventoryPalletHoldInput }>(
    PutPalletOnHoldDocument
  )

  const putPalletOnHold = useCallback(
    async (input: InventoryPalletHoldInput): Promise<boolean> => {
      const { data, error } = await executeMutation({ input })
      const output = data?.inventory.putPalletOnHold
      const hasError = error || output?.__typename === "InventoryPalletHoldFailure"

      if (hasError) {
        const message = error?.message ?? (output as InventoryPalletHoldFailure).error.message
        notify({ type: NotificationType.Error, message })

        return false
      }

      notify({ type: NotificationType.Success, message: "Pallet put on hold" })

      return true
    },
    [executeMutation, notify]
  )

  return { putPalletOnHold, isPutting: fetching }
}
